import dynamic from "next/dynamic";
import Head from "next/head";
import { useAuth } from "utils/auth";
import { useRouter } from "next/router";
import { ArticleContextProvider } from "utils/articleContext";
import { ArticleQuery, ArticleQueryVariables } from "types/api";
import { useTrackPageView } from "utils/analytics";
import { gql, useQuery } from "utils/urql";
import { throwCorrectStatus } from "utils/error";

import ArticleLayout from "components/article/ArticleLayout";
import ArticleJsonLd from "components/article/goose/ArticleJsonLd";
import ArticleMetadata from "components/article/goose/ArticleMetadata";
import ArticleRubric from "components/article/ArticleRubric";
import ArticleTitle from "components/article/ArticleTitle";
import ArticleDek from "components/article/ArticleDek";
import ArticleBylines from "components/article/ArticleBylines";
import ArticleInlineImageFigure from "components/article/ArticleInlineImageFigure";
import ArticleInlineImage from "components/article/ArticleInlineImage";
import ArticleOverflowInlineImage from "components/article/ArticleOverflowInlineImage";
import ArticleWellInlineImage from "components/article/ArticleWellInlineImage";
import ArticleLeadArt from "components/article/ArticleLeadArt";
import AboutTheAuthors from "components/article/AboutTheAuthors";
import ArticleTimestamp from "components/article/ArticleTimestamp";
import EditorsNote from "components/article/EditorsNote";
import ArticleBody from "components/article/ArticleBody";
import ArticleFoundationLine from "components/article/ArticleFoundationLine";
import ArticleBio from "components/article/ArticleBio";
import ArticleBentoRecirc from "components/article/ArticleBentoRecirc";
import ArticleShare from "components/article/ArticleShare";

export const ARTICLE_QUERY = gql`
  ${ArticleRubric.fragment}
  ${ArticleTitle.fragment}
  ${ArticleLeadArt.fragment}
  ${EditorsNote.fragment}
  ${ArticleBody.fragment}
  ${ArticleBio.fragment}
  ${ArticleMetadata.fragment}
  ${ArticleDek.fragment}
  ${ArticleBylines.fragment}
  ${ArticleInlineImageFigure.fragment}
  ${ArticleInlineImage.fragment}
  ${ArticleOverflowInlineImage.fragment}
  ${ArticleWellInlineImage.fragment}
  ${AboutTheAuthors.fragment}
  ${ArticleTimestamp.fragment}
  ${ArticleFoundationLine.fragment}
  ${ArticleJsonLd.fragment}
  ${ArticleBentoRecirc.fragment}
  ${ArticleShare.fragment}

  query ArticleQuery($id: ID!, $preview: String) {
    article(id: $id, preview: $preview) {
      __typename
      id
      isTnfCompatible
      datePublished
      title
      slug
      wordCount
      layout
      isFreelance
      isPython3
      preview
      watsonInfo {
        topCategory {
          full
          one
          two
          three
          four
        }
        categories
      }
      grapeshot {
        segments
      }
      editorialProject {
        slug
        displayName
        ...articleBentoRecircSwan
        ...articleFoundationLineSwan
      }
      ...articleMetadata
      ...articleRubric
      ...articleTitle
      ...articleSwanDek
      ...articleTimestamp
      ...articleBioSwan
      ...aboutTheAuthors
      ...articleBylines
      ...articleJsonLdData
      ...articleShare
      ...articleBodyContent

      leadArt {
        ...articleLeadArtSwan
      }
      embeds {
        type
        src
      }
      primaryChannel {
        slug
        displayName
      }
      primaryCategory {
        ... on Category {
          slug
        }
        ... on Channel {
          slug
        }
        ... on EditorialProject {
          slug
        }
      }
      categories {
        slug
      }
      channels {
        slug
      }
      editorsNote {
        ...editorsNoteSwan
      }
      advertisingInfo {
        disableHouseAd
      }
      ... on MagazineArticle {
        issue {
          issueName
          slug
        }
      }
    }
  }
`;

const DynamicPaywall = dynamic(() => import("components/paywall/Paywall"));

const DynamicNonMeteredNudge = dynamic(
  () => import("components/NonMeteredNudge")
);

const ArticleDisplay = ({ data }: { data: ArticleQuery }) => {
  const { hasPaywallAccess } = useAuth();

  useTrackPageView(data);
  return (
    <>
      <Head>
        <title>
          {data.article.seoTitle || data.article.title} - The Atlantic
        </title>
      </Head>
      <ArticleMetadata {...data.article} />
      <ArticleJsonLd {...data.article} />
      <ArticleContextProvider
        articleId={data.article.id.split(":")[1]}
        previewParam={data.article.preview}
        primaryCategorySlug={data.article.primaryCategory?.slug || ""}
      >
        <ArticleLayout {...data.article} />
      </ArticleContextProvider>
      {/* // import paywall if article is metered; if not, import non-metered nudge */}
      {data.article.hasMeter && !hasPaywallAccess ? (
        <DynamicPaywall />
      ) : (
        <DynamicNonMeteredNudge {...data.article} />
      )}
    </>
  );
};

const ArticleFetcher = () => {
  const { query } = useRouter();

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [year, month, slug, id] = query.article as Array<string>;
  const preview = typeof query.preview === "string" ? query.preview : null;

  const [{ error, data }] = useQuery<ArticleQuery, ArticleQueryVariables>({
    query: ARTICLE_QUERY,
    variables: {
      id,
      preview,
    },
  });

  if (error) {
    throwCorrectStatus({ error });
  }
  return data ? (
    <ArticleDisplay key={`article-${data.article.id}`} data={data} />
  ) : null;
};

export default ArticleFetcher;
